"use client";
import { useLanguage } from "@/contexts/language-context";
import { useLanguageSettings, Language } from "@/hooks/useLanguageSettings";
import { cn } from "@/lib/utils";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";

type Props = {
  collapsed?: boolean;
};

// Language display info for toggle
const LANGUAGE_DISPLAY: Record<Language, { short: string; label: string; flag: string }> = {
  en: { short: "EN", label: "English", flag: "🇺🇸" },
  zh: { short: "中", label: "中文", flag: "🇨🇳" },
  es: { short: "ES", label: "Español", flag: "🇪🇸" },
  ja: { short: "JP", label: "日本語", flag: "🇯🇵" },
  ko: { short: "KO", label: "한국어", flag: "🇰🇷" },
  fr: { short: "FR", label: "Français", flag: "🇫🇷" },
  de: { short: "DE", label: "Deutsch", flag: "🇩🇪" },
  pt: { short: "PT", label: "Português", flag: "🇵🇹" },
  ru: { short: "RU", label: "Русский", flag: "🇷🇺" },
};

export function LanguageToggle({ collapsed = false }: Props) {
  const { language, setLanguage } = useLanguage();
  const { currentLanguage: nativeLanguage } = useLanguageSettings();
  
  // Current UI language
  const currentLang = (language as Language) || "en";
  const isEnglish = currentLang === "en";
  
  // Get native language display info (fallback to zh if not found)
  const nativeLangInfo = LANGUAGE_DISPLAY[nativeLanguage] || LANGUAGE_DISPLAY.zh;
  
  // If native language is English, toggle between EN and ZH as fallback
  const targetLang: Language = nativeLanguage === "en" ? "zh" : nativeLanguage;
  const targetLangInfo = LANGUAGE_DISPLAY[targetLang];

  if (collapsed) {
    const label = isEnglish ? "EN" : targetLangInfo.short;
    const tooltip = isEnglish 
      ? `Switch to ${targetLangInfo.label}` 
      : "Switch to English";
    return (
      <TooltipWrapper text={tooltip}>
        <button
          type="button"
          onClick={() => setLanguage(isEnglish ? targetLang : "en")}
          className={cn(
            "h-8 w-8 rounded-full border bg-background flex items-center justify-center text-[10px] font-semibold",
            !isEnglish ? "text-foreground border-primary" : "text-muted-foreground hover:text-foreground"
          )}
          aria-label={tooltip}
        >
          {label}
        </button>
      </TooltipWrapper>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-xl border bg-background p-[3px]">
      <button
        type="button"
        className={cn(
          "px-3 py-1 text-xs rounded-[8px] transition-colors",
          isEnglish ? "bg-foreground text-primary-foreground" : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => setLanguage("en")}
        aria-pressed={isEnglish}
      >
        EN
      </button>
      <button
        type="button"
        className={cn(
          "px-2 py-1 text-xs rounded-[8px] transition-colors",
          !isEnglish ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
        )}
        onClick={() => setLanguage(targetLang)}
        aria-pressed={!isEnglish}
      >
        {targetLangInfo.label}
      </button>
    </div>
  );
}
